import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGuestsForExport } from '@/lib/hooks/useGuests';
import { generateGuestLogCSV, downloadCSV } from '@/lib/utils/export.utils';
import { eventFormClasses } from '@/lib/utils/events.utils';
import { GuestLogContent } from '@/content/GuestLogContent';

/**
 * Date range type for export filtering
 */
type DateRange = {
  dateFrom?: string;
  dateTo?: string;
};

/**
 * Get date range for today
 */
function getTodayRange(): DateRange {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  return {
    dateFrom: startOfToday.toISOString(),
    dateTo: endOfToday.toISOString(),
  };
}

/**
 * Get date range for this week (from start of week to end of today)
 */
function getThisWeekRange(): DateRange {
  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const startOfWeek = new Date(now);
  const dayOfWeek = startOfWeek.getDay();
  const diff = startOfWeek.getDate() - dayOfWeek; // Sunday = 0
  startOfWeek.setDate(diff);
  startOfWeek.setHours(0, 0, 0, 0);

  return {
    dateFrom: startOfWeek.toISOString(),
    dateTo: endOfToday.toISOString(),
  };
}

/**
 * Get date range for this month (from start of month to end of today)
 */
function getThisMonthRange(): DateRange {
  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const startOfMonth = new Date(now);
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);

  return {
    dateFrom: startOfMonth.toISOString(),
    dateTo: endOfToday.toISOString(),
  };
}

/**
 * Get date range for this quarter (from start of quarter to end of today)
 */
function getThisQuarterRange(): DateRange {
  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const startOfQuarter = new Date(now);
  const month = startOfQuarter.getMonth();
  const quarterStartMonth = Math.floor(month / 3) * 3;
  startOfQuarter.setMonth(quarterStartMonth, 1);
  startOfQuarter.setHours(0, 0, 0, 0);

  return {
    dateFrom: startOfQuarter.toISOString(),
    dateTo: endOfToday.toISOString(),
  };
}

/**
 * Get date range for this year (from start of year to end of today)
 */
function getThisYearRange(): DateRange {
  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const startOfYear = new Date(now);
  startOfYear.setMonth(0, 1);
  startOfYear.setHours(0, 0, 0, 0);

  return {
    dateFrom: startOfYear.toISOString(),
    dateTo: endOfToday.toISOString(),
  };
}

/**
 * Get "All Time" date range (no filtering)
 */
function getAllTimeRange(): DateRange {
  return { dateFrom: undefined, dateTo: undefined };
}

type ExportGuestLogDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ExportGuestLogDialog({
  open,
  onOpenChange,
}: ExportGuestLogDialogProps) {
  const [selectedRange, setSelectedRange] = useState<DateRange | null>(null);
  const [customFrom, setCustomFrom] = useState<string | undefined>();
  const [customTo, setCustomTo] = useState<string | undefined>();
  const [useCustom, setUseCustom] = useState(false);

  const { refetch: refetchForExport, isFetching: isExporting } =
    useGuestsForExport(selectedRange || undefined);

  const handlePresetClick = (presetRange: DateRange) => {
    setSelectedRange(presetRange);
    setUseCustom(false);
    setCustomFrom(undefined);
    setCustomTo(undefined);
  };

  const handleCustomRangeChange = (from?: string, to?: string) => {
    const fromValue = from ?? customFrom;
    const toValue = to ?? customTo;

    if (fromValue && toValue) {
      const fromDate = new Date(fromValue);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(toValue);
      toDate.setHours(23, 59, 59, 999);

      setSelectedRange({
        dateFrom: fromDate.toISOString(),
        dateTo: toDate.toISOString(),
      });
      setUseCustom(true);
    } else {
      setSelectedRange(null);
      setUseCustom(false);
    }
  };

  const handleExport = async () => {
    if (!selectedRange) return;

    try {
      const { data } = await refetchForExport();
      if (data && data.length > 0) {
        const csvContent = generateGuestLogCSV(data);
        const dateStr =
          useCustom && customFrom && customTo
            ? `${customFrom}_to_${customTo}`
            : new Date().toISOString().split('T')[0];
        const filename = `guest_log_${dateStr}.csv`;
        downloadCSV(csvContent, filename);
        onOpenChange(false);
        // Reset state
        setSelectedRange(null);
        setCustomFrom(undefined);
        setCustomTo(undefined);
        setUseCustom(false);
      }
    } catch (error) {
      console.error('Failed to export guest log', error);
    }
  };

  const canExport =
    selectedRange !== null && (!useCustom || (customFrom && customTo));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{GuestLogContent.exportDialog.title}</DialogTitle>
          <DialogDescription>
            {GuestLogContent.exportDialog.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preset Buttons */}
          <div>
            <label className="text-small text-slate-400 mb-2 block">
              {GuestLogContent.exportDialog.presetsLabel}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(getTodayRange())}
                className={
                  selectedRange &&
                  !useCustom &&
                  selectedRange.dateFrom === getTodayRange().dateFrom
                    ? 'border-purple-400'
                    : ''
                }
              >
                {GuestLogContent.exportDialog.presets.today}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(getThisWeekRange())}
                className={
                  selectedRange &&
                  !useCustom &&
                  selectedRange.dateFrom === getThisWeekRange().dateFrom
                    ? 'border-purple-400'
                    : ''
                }
              >
                {GuestLogContent.exportDialog.presets.thisWeek}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(getThisMonthRange())}
                className={
                  selectedRange &&
                  !useCustom &&
                  selectedRange.dateFrom === getThisMonthRange().dateFrom
                    ? 'border-purple-400'
                    : ''
                }
              >
                {GuestLogContent.exportDialog.presets.thisMonth}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(getThisQuarterRange())}
                className={
                  selectedRange &&
                  !useCustom &&
                  selectedRange.dateFrom === getThisQuarterRange().dateFrom
                    ? 'border-purple-400'
                    : ''
                }
              >
                {GuestLogContent.exportDialog.presets.thisQuarter}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(getThisYearRange())}
                className={
                  selectedRange &&
                  !useCustom &&
                  selectedRange.dateFrom === getThisYearRange().dateFrom
                    ? 'border-purple-400'
                    : ''
                }
              >
                {GuestLogContent.exportDialog.presets.thisYear}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(getAllTimeRange())}
                className={
                  selectedRange &&
                  !useCustom &&
                  selectedRange.dateFrom === undefined
                    ? 'border-purple-400'
                    : ''
                }
              >
                {GuestLogContent.exportDialog.presets.allTime}
              </Button>
            </div>
          </div>

          {/* Custom Date Range */}
          <div className="space-y-3">
            <label className="text-small text-slate-400 block">
              {GuestLogContent.exportDialog.customLabel}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label
                  className={eventFormClasses.label}
                  htmlFor="export-from-date"
                >
                  {GuestLogContent.exportDialog.customFrom}
                </label>
                <Input
                  id="export-from-date"
                  type="date"
                  value={customFrom ?? ''}
                  onChange={(e) => {
                    setCustomFrom(e.target.value || undefined);
                    handleCustomRangeChange(e.target.value || undefined, customTo);
                  }}
                  className={eventFormClasses.input}
                />
              </div>
              <div className="space-y-2">
                <label
                  className={eventFormClasses.label}
                  htmlFor="export-to-date"
                >
                  {GuestLogContent.exportDialog.customTo}
                </label>
                <Input
                  id="export-to-date"
                  type="date"
                  value={customTo ?? ''}
                  onChange={(e) => {
                    setCustomTo(e.target.value || undefined);
                    handleCustomRangeChange(customFrom, e.target.value || undefined);
                  }}
                  className={eventFormClasses.input}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            className="mt-3"
            variant="outline"
            size="lg"
            onClick={() => {
              onOpenChange(false);
              setSelectedRange(null);
              setCustomFrom(undefined);
              setCustomTo(undefined);
              setUseCustom(false);
            }}
          >
            {GuestLogContent.exportDialog.cancel}
          </Button>
          <Button
            size="lg"
            onClick={handleExport}
            disabled={!canExport || isExporting}
          >
            {isExporting ? 'Exporting...' : GuestLogContent.exportDialog.export}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

